import { useEffect, useState, useCallback } from "react"
import {
  Card,
  Tabs,
  Button,
  Space,
  Table,
  Tag,
  Modal,
  Form,
  Input,
  Upload,
  message,
  Spin,
  Typography,
  Popconfirm,
  Descriptions,
} from "antd"
import {
  UploadOutlined,
  FileOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  InboxOutlined,
} from "@ant-design/icons"
import { getDocuments, uploadDocument, updateDocument, deleteDocument } from "../../services/DocumentsServices"
import { useAuth } from "../../contexts/authContext/index"

const { TabPane } = Tabs
const { Dragger } = Upload
const { Title, Text } = Typography
const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL

const DocumentsPage = () => {
  const [vendorDocuments, setVendorDocuments] = useState([])
  const [clientDocuments, setClientDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploadModalVisible, setUploadModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [currentRole, setCurrentRole] = useState("vendedor")
  const [currentDocument, setCurrentDocument] = useState(null)
  const [fileList, setFileList] = useState([])
  const [uploading, setUploading] = useState(false)
  const { getToken } = useAuth()
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()



  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true)
      const token = await getToken()
  
      const vendorDocs = await getDocuments(token, "vendedor")
      setVendorDocuments(vendorDocs)
  
      const clientDocs = await getDocuments(token, "cliente")
      setClientDocuments(clientDocs)
    } catch (error) {
      console.error("Error fetching documents:", error)
      message.error("Error al cargar los documentos")
    } finally {
      setLoading(false)
    }
  }, [getToken]) 

    useEffect(() => {
        fetchDocuments()
      }, [fetchDocuments])

  const handleUpload = async (values) => {
    if (fileList.length === 0) {
      message.error("Por favor seleccione un archivo")
      return
    }

    const formData = new FormData()
    formData.append("file", fileList[0])
    formData.append("role", currentRole)
    formData.append("description", values.description)

    try {
      setUploading(true)
      const token = await getToken()
      await uploadDocument(token, formData)

      message.success("Documento subido exitosamente")
      setUploadModalVisible(false)
      form.resetFields()
      setFileList([])
      fetchDocuments()
    } catch (error) {
      console.error("Error uploading document:", error)
      message.error("Error al subir el documento")
    } finally {
      setUploading(false)
    }
  }

  const handleEdit = async (values) => {
    try {
      setUploading(true)
      const token = await getToken()

      // Create update data
      const updateData = {
        role: values.role,
        description: values.description,
      }

      await updateDocument(token, currentDocument.id, updateData)

      message.success("Documento actualizado exitosamente")
      setEditModalVisible(false)
      editForm.resetFields()
      fetchDocuments()
    } catch (error) {
      console.error("Error updating document:", error)
      message.error("Error al actualizar el documento")
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (documentId) => {
    try {
      const token = await getToken()
      await deleteDocument(token, documentId)

      message.success("Documento eliminado exitosamente")
      fetchDocuments()
    } catch (error) {
      console.error("Error deleting document:", error)
      message.error("Error al eliminar el documento")
    }
  }

  const handleView = async (document) => {
    try {
      const downloadUrl = `${REACT_APP_BFF_URL}/api/documents/${document.id}/download`
      
      // Open the document in a new tab
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error("Error downloading document:", error)
      message.error("Error al descargar el documento")
    }
  }

  const showUploadModal = (role) => {
    setCurrentRole(role)
    setUploadModalVisible(true)
  }

  const showEditModal = (document) => {
    setCurrentDocument(document)
    editForm.setFieldsValue({
      role: document.role,
      description: document.description,
    })
    setEditModalVisible(true)
  }

  const showViewModal = (document) => {
    setCurrentDocument(document)
    setViewModalVisible(true)
  }

  const uploadProps = {
    onRemove: () => {
      setFileList([])
    },
    beforeUpload: (file) => {
      // Only allow PDF files
      const isPDF = file.type === "application/pdf"
      if (!isPDF) {
        message.error("Solo se permiten archivos PDF")
        return Upload.LIST_IGNORE
      }

      setFileList([file])
      return false
    },
    fileList,
  }

  const getDocumentStatusTag = (status) => {
    if (!status) return <Tag color="default">Sin estado</Tag>

    const statusColors = {
      active: "green",
      pending: "gold",
      expired: "red",
      draft: "blue",
    }

    return <Tag color={statusColors[status.toLowerCase()] || "default"}>{status}</Tag>
  }

  const columns = [
    {
      title: "Documento",
      dataIndex: "filename",
      key: "filename",
      render: (text) => (
        <Space>
          <FileOutlined />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Fecha de subida",
      dataIndex: "uploadDate",
      key: "uploadDate",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      render: (status) => getDocumentStatusTag(status),
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button type="primary" icon={<EyeOutlined />} size="small" onClick={() => showViewModal(record)}>
            Ver
          </Button>
          <Button type="default" icon={<EditOutlined />} size="small" onClick={() => showEditModal(record)}>
            Editar
          </Button>
          <Popconfirm
            title="¿Está seguro de eliminar este documento?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button type="danger" icon={<DeleteOutlined />} size="small">
              Eliminar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const renderDocumentSection = (role, documents) => {
    const hasDocument = documents && documents.length > 0

    return (
      <div className="document-section">
        <div
          className="document-header"
          style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <Title level={4}>{role === "vendedor" ? "Documentos de Vendedor" : "Documentos de Cliente"}</Title>
          {!hasDocument && (
            <Button type="primary" icon={<UploadOutlined />} onClick={() => showUploadModal(role)}>
              Subir Documento
            </Button>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
          </div>
        ) : hasDocument ? (
          <>
            <Table dataSource={documents} columns={columns} rowKey="id" pagination={false} />
          </>
        ) : (
          <Card>
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <FileOutlined style={{ fontSize: 48, color: "#ccc", marginBottom: 16 }} />
              <p>No hay documentos disponibles</p>
            </div>
          </Card>
        )}
      </div>
    )
  }

  return (
    <Card title="Gestión de Documentos">
      <Tabs defaultActiveKey="1">
        <TabPane tab="Vendedor" key="1">
          {renderDocumentSection("vendedor", vendorDocuments)}
        </TabPane>
        <TabPane tab="Cliente" key="2">
          {renderDocumentSection("cliente", clientDocuments)}
        </TabPane>
      </Tabs>

      {/* Upload Document Modal */}
      <Modal
        title={`Subir Documento de ${currentRole === "vendedor" ? "Vendedor" : "Cliente"}`}
        open={uploadModalVisible}
        onCancel={() => setUploadModalVisible(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleUpload}>
          <Form.Item
            name="file"
            label="Documento"
            rules={[{ required: true, message: "Por favor seleccione un archivo" }]}
          >
            <Dragger {...uploadProps} maxCount={1}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Haga clic o arrastre un archivo a esta área para subirlo</p>
              <p className="ant-upload-hint">Solo se permiten archivos PDF</p>
            </Dragger>
          </Form.Item>
          <Form.Item
            name="description"
            label="Descripción"
            rules={[{ required: true, message: "Por favor ingrese una descripción" }]}
          >
            <Input.TextArea rows={4} placeholder="Ingrese una descripción para el documento" />
          </Form.Item>
          <Form.Item>
            <div style={{ textAlign: "right" }}>
              <Button style={{ marginRight: 8 }} onClick={() => setUploadModalVisible(false)}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={uploading}>
                Subir
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Document Modal */}
      <Modal title="Editar Documento" open={editModalVisible} onCancel={() => setEditModalVisible(false)} footer={null}>
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="role" label="Tipo" rules={[{ required: true, message: "Por favor seleccione un tipo" }]}>
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="description"
            label="Descripción"
            rules={[{ required: true, message: "Por favor ingrese una descripción" }]}
          >
            <Input.TextArea rows={4} placeholder="Ingrese una descripción para el documento" />
          </Form.Item>
          <Form.Item>
            <div style={{ textAlign: "right" }}>
              <Button style={{ marginRight: 8 }} onClick={() => setEditModalVisible(false)}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={uploading}>
                Actualizar
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para ver detalles del documento */}
      <Modal
        title="Ver Documento"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="download" type="primary" onClick={() => currentDocument && handleView(currentDocument)}>
            Descargar
          </Button>,
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Cerrar
          </Button>
        ]}
        width={800}
      >
        {currentDocument && (
          <div>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Nombre:">{currentDocument.filename}</Descriptions.Item>
              <Descriptions.Item label="Descripción:">{currentDocument.description || "Sin descripción"}</Descriptions.Item>
              <Descriptions.Item label="Fecha de subida:">
                {currentDocument.uploadDate ? currentDocument.uploadDate.toLocaleString() : "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Estado:">
                <Tag color="green">{currentDocument.status}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Tipo:">{currentDocument.role}</Descriptions.Item>
            </Descriptions>
            
            <div style={{ marginTop: 16 }}>
              <Text strong>Vista previa del documento:</Text>
              <div style={{ 
                marginTop: 8, 
                border: '1px solid #d9d9d9', 
                borderRadius: 6, 
                height: 400,
                overflow: 'hidden'
              }}>
                <iframe
                  src={`${REACT_APP_BFF_URL}/api/documents/${currentDocument.id}/preview`}
                  style={{ 
                    width: '100%', 
                    height: '100%', 
                    border: 'none' 
                  }}
                  title="Document preview"
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </Card>
  )
}

export default DocumentsPage
